import { logger } from '@heidi/runtime/logger';
import { TrackingKey } from '@heidi/runtime/performance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'jotai';
import type { CreateExampleViaChatRequest } from '../../contracts';
import {
  GENERATE_EXAMPLE_VIA_CHAT_STREAMING_KEY,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '../../contracts';
import { exampleGenerationStreamStateAtomFamily } from './atoms';
import { trackStreamRun } from './streamPerfTracking';
import {
  appendToVersionControlField,
  createStreamStateHandlers,
  updateStreamStates,
  updateVersionControlInQueryData,
} from './streamStateHelpers';
import { streamExampleViaChatGeneration } from './streaming';
import { type AnimationType, StreamStatus } from './types';

export { GENERATE_EXAMPLE_VIA_CHAT_STREAMING_KEY };

export interface GenerateExampleViaChatMutationRequest extends CreateExampleViaChatRequest {
  template_creation_record_id: string;
  target_version_control_id: string;
  animationType: AnimationType;
}

export const useMutateGenerateExampleViaChat = () => {
  const queryClient = useQueryClient();
  const store = useStore();

  return useMutation({
    mutationKey: [GENERATE_EXAMPLE_VIA_CHAT_STREAMING_KEY],

    onMutate: ({
      template_creation_record_id: templateCreationRecordId,
      target_version_control_id: versionControlId,
      animationType,
    }) => {
      const streamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });
      updateStreamStates({
        store,
        atoms: [streamStateAtom],
        status: StreamStatus.WAITING_START,
        animationType,
      });
      return { streamStateAtom };
    },

    mutationFn: async ({
      template_creation_record_id: templateCreationRecordId,
      target_version_control_id: versionControlId,
      current_example,
      user_request,
      animationType,
    }: GenerateExampleViaChatMutationRequest): Promise<string> => {
      const streamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });

      const streamState = store.get(streamStateAtom);
      const queryKey = [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }];

      return trackStreamRun(
        TrackingKey.TEMPLATE_EXAMPLE_CHAT_EDIT,
        { animationType },
        async (markFirstToken) => {
          let accumulatedExample = '';

          let isFirstChunk = true;

          await streamExampleViaChatGeneration(
            {
              data: {
                current_example,
                user_request,
              },
              templateCreationRecordId,
              targetVersionControlId: versionControlId,
              signal: streamState.abortController.signal,
            },
            createStreamStateHandlers({
              store,
              streamStateAtom,
              onMessageCallback: (chunk) => {
                if (isFirstChunk) {
                  markFirstToken();
                  updateStreamStates({
                    store,
                    atoms: [streamStateAtom],
                    status: StreamStatus.STREAMING,
                  });
                  updateVersionControlInQueryData({
                    queryClient,
                    queryKey,
                    versionControlId,
                    field: 'example_note',
                    updater: () => '',
                  });
                  isFirstChunk = false;
                }
                accumulatedExample += chunk.data;
                appendToVersionControlField({
                  queryClient,
                  queryKey,
                  versionControlId,
                  field: 'example_note',
                  chunk,
                });
              },
            })
          );

          return accumulatedExample;
        }
      );
    },
    onSuccess: (_, __, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.IDLE,
        });
      }
    },
    onError: (error: Error, _, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.ERROR,
        });
      }
      logger.error(error);
    },
  });
};
